/**
 * Resolve Chat Transport Decision
 *
 * Answers which chat transport a page load boots against, from the persisted
 * `testMode` slice. Bootstrap calls this once in `init()` and passes the answer
 * down to the widget bundle — which is why this lives apart from
 * `configureGateways`: the read happens in the always-loaded tier, the gateway
 * it decides is built in the widget tier.
 *
 * `init()` rather than later is about caching one answer per page load, not
 * about beating any particular downstream step: the auth machine runs in the
 * widget tier, long after this. One consequence of being this early is that
 * `_serverConfig` does not exist yet, so the persisted `orgId` is not checked
 * against the org the widget is actually booting for; cross-checking it would
 * mean resolving after the config fetch.
 *
 * The read goes through `getPersistedState()` rather than the store, because
 * the store does not exist yet: bootstrap runs before the widget bundle is
 * fetched, and `store.persist.rehydrate()` is `startFullWidget`'s first act.
 * That rehydration is where the slice gets validated, through
 * `mergePersistedState`, before anything renders from it. This read only has to
 * choose between two transports, so it takes the value as it finds it — the
 * same way `getPersistedUserCredentials` reads the persisted token a few lines
 * away in bootstrap.
 */

import type { ChatTransportDecision } from '@/domain/gateway';
import { isTestModeUsable } from '@/domain/testMode';
import { getPersistedState } from '@/stores/persistedStorage';

/**
 * Which transport this page load gets.
 *
 * `isTestModeUsable` pairs the active and TTL checks, so a widget that was never
 * in test mode cannot pass on the strength of not having expired. Expiry is
 * applied again by `mergePersistedState` when the store rehydrates: the two run
 * off different clocks, so a TTL elapsing in between leaves a `gladosTester`
 * decision beside a store whose `testMode.active` is false.
 */
export function resolveChatTransportDecision(): ChatTransportDecision {
  const testMode = getPersistedState()?.testMode;
  return testMode && isTestModeUsable(testMode) ? 'gladosTester' : 'production';
}
